export interface CompositePricingInput {
  basePrice?: number | null;
  vatPercentage?: number | null;
  vatAmount?: number | null;
  extraFee1Percent?: number | null;
  extraFee1Amount?: number | null;
  extraFee1VatPercent?: number | null;
  extraFee1VatAmount?: number | null;
  extraFee1Fixed?: number | null;
  extraFee2Percent?: number | null;
  extraFee2Amount?: number | null;
  extraFee2VatPercent?: number | null;
  extraFee2VatAmount?: number | null;
  extraFee2Fixed?: number | null;
  totalUnitPrice?: number | null;
  price?: number | string | null;
}

export interface CompositePricingBreakdown {
  basePrice: number;
  vatAmount: number;
  priceWithVat: number;
  extraFee1Amount: number;
  extraFee1VatAmount: number;
  extraFee1Fixed: number;
  extraFee2Amount: number;
  extraFee2VatAmount: number;
  extraFee2Fixed: number;
  totalUnitPrice: number;
}

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function resolveAmount(
  override: number | null | undefined,
  calculated: number,
): number {
  if (override != null && override >= 0) {
    return roundMoney(override);
  }
  return roundMoney(calculated);
}

export function hasCompositePricingConfig(input: CompositePricingInput): boolean {
  return (
    (input.basePrice != null && input.basePrice > 0) ||
    (input.extraFee1Percent ?? 0) > 0 ||
    (input.extraFee1Fixed ?? 0) > 0 ||
    (input.extraFee2Percent ?? 0) > 0 ||
    (input.extraFee2Fixed ?? 0) > 0 ||
    input.extraFee1Amount != null ||
    input.extraFee2Amount != null
  );
}

export function calculateCompositePricing(
  input: CompositePricingInput,
): CompositePricingBreakdown {
  const basePrice = roundMoney(Math.max(0, input.basePrice ?? 0));
  const vatPercentage = input.vatPercentage ?? 0;

  const vatAmount = resolveAmount(
    input.vatAmount,
    basePrice * vatPercentage / 100,
  );
  const priceWithVat = roundMoney(basePrice + vatAmount);

  const extraFee1Percent = input.extraFee1Percent ?? 0;
  const extraFee1Amount = resolveAmount(
    input.extraFee1Amount,
    basePrice * extraFee1Percent / 100,
  );
  const extraFee1VatPercent = input.extraFee1VatPercent ?? 0;
  const extraFee1VatAmount = resolveAmount(
    input.extraFee1VatAmount,
    extraFee1Amount * extraFee1VatPercent / 100,
  );
  const extraFee1Fixed = roundMoney(Math.max(0, input.extraFee1Fixed ?? 0));

  const extraFee2Percent = input.extraFee2Percent ?? 0;
  const extraFee2Amount = resolveAmount(
    input.extraFee2Amount,
    basePrice * extraFee2Percent / 100,
  );
  const extraFee2VatPercent = input.extraFee2VatPercent ?? 0;
  const extraFee2VatAmount = resolveAmount(
    input.extraFee2VatAmount,
    extraFee2Amount * extraFee2VatPercent / 100,
  );
  const extraFee2Fixed = roundMoney(Math.max(0, input.extraFee2Fixed ?? 0));

  const calculatedTotal = roundMoney(
    basePrice +
      vatAmount +
      extraFee1Amount +
      extraFee1VatAmount +
      extraFee1Fixed +
      extraFee2Amount +
      extraFee2VatAmount +
      extraFee2Fixed,
  );

  const totalUnitPrice = resolveAmount(
    input.totalUnitPrice,
    calculatedTotal,
  );

  return {
    basePrice,
    vatAmount,
    priceWithVat,
    extraFee1Amount,
    extraFee1VatAmount,
    extraFee1Fixed,
    extraFee2Amount,
    extraFee2VatAmount,
    extraFee2Fixed,
    totalUnitPrice,
  };
}

/** Formula-based values ignoring manual amount overrides (for "Calculated" hints). */
export function calculateCompositePricingDefaults(
  input: CompositePricingInput,
): CompositePricingBreakdown {
  return calculateCompositePricing({
    ...input,
    vatAmount: undefined,
    extraFee1Amount: undefined,
    extraFee1VatAmount: undefined,
    extraFee2Amount: undefined,
    extraFee2VatAmount: undefined,
    totalUnitPrice: undefined,
  });
}

export type CompositePricingAmountField =
  | 'vatAmount'
  | 'extraFee1Amount'
  | 'extraFee1VatAmount'
  | 'extraFee2Amount'
  | 'extraFee2VatAmount'
  | 'totalUnitPrice';

export type CompositePricingDriverField =
  | 'basePrice'
  | 'extraFee1Percent'
  | 'extraFee1Amount'
  | 'extraFee1VatPercent'
  | 'extraFee1VatAmount'
  | 'extraFee1Fixed'
  | 'extraFee2Percent'
  | 'extraFee2Amount'
  | 'extraFee2VatPercent'
  | 'extraFee2VatAmount'
  | 'extraFee2Fixed'
  | 'vatAmount'
  | 'totalUnitPrice'
  | 'price';

/** Clear dependent amount overrides when a driver field changes so values auto-recalculate. */
export function applyCompositeFieldChange<T extends CompositePricingInput>(
  data: T,
  field: CompositePricingDriverField,
  value: number | null | undefined,
): T {
  const next: T = { ...data, [field]: value ?? undefined } as T;

  if (field === 'vatAmount') {
    next.totalUnitPrice = undefined;
    return next;
  }
  if (field === 'totalUnitPrice') {
    return next;
  }

  next.totalUnitPrice = undefined;

  if (field === 'basePrice') {
    next.vatAmount = undefined;
    next.extraFee1Amount = undefined;
    next.extraFee1VatAmount = undefined;
    next.extraFee2Amount = undefined;
    next.extraFee2VatAmount = undefined;
  } else if (field === 'extraFee1Percent' || field === 'extraFee1Fixed') {
    next.extraFee1Amount = undefined;
    next.extraFee1VatAmount = undefined;
  } else if (field === 'extraFee1Amount' || field === 'extraFee1VatPercent') {
    next.extraFee1VatAmount = undefined;
  } else if (field === 'extraFee1VatAmount') {
    // manual override — keep value, only total was cleared above
  } else if (field === 'extraFee2Percent' || field === 'extraFee2Fixed') {
    next.extraFee2Amount = undefined;
    next.extraFee2VatAmount = undefined;
  } else if (field === 'extraFee2Amount' || field === 'extraFee2VatPercent') {
    next.extraFee2VatAmount = undefined;
  } else if (field === 'extraFee2VatAmount') {
    // manual override — keep value, only total was cleared above
  }

  return next;
}

/** If stored amount matches the formula result, treat as auto-calculated (no manual override). */
export function resolveCompositeAmountOverride(
  stored: number | null | undefined,
  calculated: number,
): number | undefined {
  if (stored == null) {
    return undefined;
  }
  if (roundMoney(stored) === roundMoney(calculated)) {
    return undefined;
  }
  return stored;
}

/** Clear amount overrides when VAT % changes. */
export function applyVatPercentageChange<T extends CompositePricingInput>(
  data: T,
  vatPercentage: number,
): T {
  return {
    ...data,
    vatPercentage,
    vatAmount: undefined,
    totalUnitPrice: undefined,
  };
}

export function getCompositeUnitPrice(input: CompositePricingInput): number {
  if (hasCompositePricingConfig(input)) {
    return calculateCompositePricing(input).totalUnitPrice;
  }
  const normalizedPrice = typeof input.price === 'string'
    ? parseFloat(input.price) || 0
    : input.price;
  if (normalizedPrice != null && normalizedPrice > 0) {
    return roundMoney(normalizedPrice);
  }
  if (input.totalUnitPrice != null && input.totalUnitPrice > 0) {
    return roundMoney(input.totalUnitPrice);
  }
  const base = input.basePrice ?? 0;
  const vatPct = input.vatPercentage ?? 0;
  return roundMoney(base * (1 + vatPct / 100));
}

export const EMPTY_COMPOSITE_PRICING: CompositePricingInput = {
  extraFee1Percent: 0,
  extraFee1Fixed: 0,
  extraFee2Percent: 0,
  extraFee2Fixed: 0,
};

export interface CompositeFeeNameInput {
  extraFee1Name?: string | null;
  extraFee2Name?: string | null;
  extraFee1Percent?: number | null;
  extraFee1Amount?: number | null;
  extraFee1Fixed?: number | null;
  extraFee2Percent?: number | null;
  extraFee2Amount?: number | null;
  extraFee2Fixed?: number | null;
}

export function hasExtraFee1(config: CompositeFeeNameInput): boolean {
  return (
    (config.extraFee1Percent ?? 0) > 0 ||
    (config.extraFee1Fixed ?? 0) > 0 ||
    (config.extraFee1Amount ?? 0) > 0
  );
}

export function hasExtraFee2(config: CompositeFeeNameInput): boolean {
  return (
    (config.extraFee2Percent ?? 0) > 0 ||
    (config.extraFee2Fixed ?? 0) > 0 ||
    (config.extraFee2Amount ?? 0) > 0
  );
}

export function buildCompositeBillServiceLines(
  categoryName: string,
  config: CompositeFeeNameInput,
): string[] {
  const lines = [categoryName];
  if (config.extraFee1Name?.trim() && hasExtraFee1(config)) {
    lines.push(config.extraFee1Name.trim());
  }
  if (config.extraFee2Name?.trim() && hasExtraFee2(config)) {
    lines.push(config.extraFee2Name.trim());
  }
  return lines;
}

export function formatCompositeBillServiceName(
  categoryName: string,
  config: CompositeFeeNameInput,
): string {
  return buildCompositeBillServiceLines(categoryName, config).join(' / ');
}
